/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable import/no-cycle */
import { defineStore } from 'pinia';
import {
  ICase,
  IData,
  IFolder,
  IMedia,
  IResponse,
  IPagination,
  ISaveCasePayload,
  ISharedLable,
  ISpacesContact,
  ICreateFolder,
  IMixedData,
  IList,
  IImageAddLabels,
  ICaseFilesManage,
  IShared,
  IDataLoading,
  IProfile,
} from '@/types/interfaces';
import MediaService from '@/services/MediaService';
import DriveService from '../services/DriveService';
import {
  ENCODING_TYPES, MEDIA_TYPES, MESSAGE_ATTACHMENT_TYPE, MIXED_DATA_TYPE, PHOTO_VIEW_LAYOUT, SHARE_ACCESS,
} from '@/types/enums';
import { useMainStore } from '@/store/mainStore';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';

export type DriveState = {
  files: IMedia[]

  photos: IPagination<IMedia[]>
  searchPhotos: IMedia[]

  cases: IPagination<ICase[]>
  publicCases: IPagination<ICase[]>
  userPublicCases: IPagination<ICase[]>
  searchCases: ICase[]

  mixedData: IPagination<IMixedData[]>
  searchMixedData: IMixedData[]

  networkPhotos: IPagination<IMedia[]>
  networkFiles: IMedia[]
  networkCases: IPagination<ICase[]>
  networkMixedData: IPagination<IMixedData[]>
  dataLoading: IDataLoading

  currentCase: ICase,
  currentFolder: IFolder | null

  appPhotoViewMedia: IMedia | undefined,
  fullscreenPhoto: IMedia | undefined,
  fullscreenFile: IMedia | undefined,
  photoList: IMedia[],

  isPhotoSelector: boolean,
  isMixedSelector: boolean,
  isCaseSelector: boolean,

  bufferCurrentCaseTitle: string;
  caseTitleValidation: string;
  casePatientNameValidation: string;
  casePatientIdValidation: string;
  caseDescriptionValidation: string;

  search: string

  photoViewLayout: number
  isGridFolderView: boolean
  isGridCasesView: boolean
  isGridPhotoView: boolean
  folderFileAmount: number[]
}

export const useDriveStore = defineStore({
  id: 'driveStore',

  state: () => ({
    photos: {
      data: [],
      page: 0,
      lastPage: -1,
    },
    searchPhotos: [],
    files: [],
    cases: {
      data: [],
      page: 0,
      lastPage: -1,
    },
    publicCases: {
      data: [],
      page: 0,
      lastPage: -1,
    },
    userPublicCases: {
      data: [],
      page: 0,
      lastPage: -1,
    },
    searchCases: [],
    mixedData: {
      data: [],
      page: 0,
      lastPage: -1,
    },
    searchMixedData: [],
    networkPhotos: {
      data: [],
      page: 0,
      lastPage: -1,
    },
    networkFiles: [],
    networkCases: {
      data: [],
      page: 0,
      lastPage: -1,
    },
    networkMixedData: {
      data: [],
      page: 0,
      lastPage: -1,
    },
    dataLoading: {
      cases: false,
      publicCases: false,
      userPublicCases: false,
      photos: false,
      mixed: false,
      networkCases: false,
    },
    currentCase: {
      id: null,
      created: null,
      updated: null,
      title: '',
      description: '',
      patientId: '',
      patientName: '',
      notes: '',
      files: [],
      labels: [],
      photos: [],
      videos: [],
      folder: {},
      folderId: null,
      owner: {} as ISpacesContact,
      ownerId: {
        internalId: '',
      },
      share: null,
      publicAccessed: false,
      publicUrl: '',
      root: false,
      profile: {} as IProfile,
    },
    currentFolder: null,

    appPhotoViewMedia: undefined,
    fullscreenPhoto: undefined,
    fullscreenFile: undefined,
    photoList: [],

    isPhotoSelector: false,
    isMixedSelector: false,
    isCaseSelector: false,

    bufferCurrentCaseTitle: '',
    caseTitleValidation: '',
    caseDescriptionValidation: '',
    casePatientNameValidation: '',
    casePatientIdValidation: '',

    search: '',
    photoViewLayout: 0,
    isGridFolderView: false,
    isGridCasesView: false,
    isGridPhotoView: false,
    folderFileAmount: [],
  } as DriveState),

  actions: {
    switchPhotoView() {
      this.photoViewLayout += 1;
      if (this.photoViewLayout >= Object.keys(PHOTO_VIEW_LAYOUT).length / 2) {
        this.photoViewLayout = 0;
      }
    },

    async fetchPhotos(): Promise<IMedia[]> {
      this.dataLoading.photos = true;
      const mainStore = useMainStore();
      if (mainStore.isMobile) {
        return MediaService.getMediaByType([MEDIA_TYPES.PHOTO, MEDIA_TYPES.VIDEO], this.photos.page, '')
          .then((response) => {
            if (response.success) {
              const items = response.results.data.items.filter((it) => !it.deleted);
              if (this.photos.page > 0) {
                this.photos.data.push(...items);
              } else {
                this.photos.data = items || [];
              }
              this.photos.page += 1;
              this.photos.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
            }
            this.dataLoading.photos = false;
            return this.photos.data;
          });
      } return MediaService.getDesktopMediaByType([MEDIA_TYPES.PHOTO, MEDIA_TYPES.VIDEO], this.photos.page, '')
        .then((response) => {
          if (response.success) {
            const items = response.results.data.items.filter((it) => !it.deleted);
            if (this.photos.page > 0) {
              this.photos.data.push(...items);
            } else {
              this.photos.data = items || [];
            }
            this.photos.page += 1;
            this.photos.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
          }
          this.dataLoading.photos = false;
          return this.photos.data;
        });
    },

    async fetchCurrentFile(id: string) {
      return MediaService.getFileInfo(id).then((response) => {
        this.fullscreenFile = response.results.data;
      });
    },

    async fetchCurrentPhoto(id: string) {
      return MediaService.getFileInfo(id).then((response) => {
        this.fullscreenPhoto = response.results.data;
      });
    },

    async fetchFiles(page: number, buyPass = false): Promise<IResponse<IData<IList<IMedia>>>> {
      const mainStore = useMainStore();
      if (mainStore.isMobile) {
        return MediaService.getMediaByType(MEDIA_TYPES.FILES, page, this.search, buyPass).then((response) => {
          if (response.success) {
            const items = response.results.data.items.filter((it) => !it.deleted);
            if (page > 0) {
              this.files.push(...items);
            } else {
              this.files = items || [];
            }
          }
          return response;
        });
      } return MediaService.getDesktopMediaByType(MEDIA_TYPES.FILES, page, this.search, buyPass).then((response) => {
        if (response.success) {
          const items = response.results.data.items.filter((it) => !it.deleted);
          if (page > 0) {
            this.files.push(...items);
          } else {
            this.files = items || [];
          }
        }
        return response;
      });
    },

    async fetchCases(initial = false): Promise<ICase[]> {
      const mainStore = useMainStore();
      this.dataLoading.cases = true;
      if (mainStore.isMobile) {
        return DriveService.getCases(this.cases.page, '').then((response) => {
          if (response.success) {
            const cases = response.results?.data.items.filter((cas) => !cas.deleted);
            if (this.cases.page > 0 && !initial) {
              this.cases.data.push(...cases);
            } else {
              this.cases.data = cases || [];
            }
            this.cases.page += 1;
            this.cases.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
          }
          this.dataLoading.cases = false;
          return this.cases.data;
        });
      }
      return DriveService.getDesktopCases(this.cases.page, '').then((response) => {
        if (response.success) {
          const cases = response.results?.data.items.filter((cas) => !cas.deleted);
          if (this.cases.page > 0) {
            this.cases.data.push(...cases);
          } else {
            this.cases.data = cases || [];
          }
          this.cases.page += 1;
          this.cases.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
        }
        this.dataLoading.cases = false;
        return this.cases.data;
      });
    },

    async fetchPublicCases(): Promise<ICase[]> {
      this.dataLoading.publicCases = true;
      return DriveService.getPublicCases(this.publicCases.page, '').then((response) => {
        if (response.success) {
          const cases = response.results?.data.items.filter((cas) => !cas.deleted);
          if (this.publicCases.page > 0) {
            this.publicCases.data.push(...cases);
          } else {
            this.publicCases.data = cases || [];
          }
          this.publicCases.page += 1;
          this.publicCases.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
        }
        this.dataLoading.publicCases = false;
        return this.publicCases.data;
      });
    },

    async fetchUserPublicCases(accountId): Promise<ICase[]> {
      this.dataLoading.userPublicCases = true;
      return DriveService.getUserPublicCases(this.userPublicCases.page, '', accountId).then((response) => {
        if (response.success) {
          const cases = response.results?.data.items.filter((cas) => !cas.deleted);
          if (this.userPublicCases.page > 0) {
            this.userPublicCases.data.push(...cases);
          } else {
            this.userPublicCases.data = cases || [];
          }
          this.userPublicCases.page += 1;
          this.userPublicCases.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
        }
        this.dataLoading.userPublicCases = false;
        return this.userPublicCases.data;
      });
    },

    async fetchNetworkPhotos(initial = false): Promise<IMedia[]> {
      const mainStore = useMainStore();
      if (initial) { this.networkPhotos.page = 0; }
      if (mainStore.isMobile) {
        return DriveService.getNetworkPhotos(this.networkPhotos.page, '')
          .then((response) => {
            if (response.success) {
              const photos = response.results.data.items.map((item) => ({
                ...item.file as IMedia,
                sharedTime: item.shared,
                root: item.root,
                shareSettings: item.shareSettings,
              }));
              if (initial) { this.networkPhotos.page = 0; }
              if (this.networkPhotos.page > 0) {
                this.networkPhotos.data.push(...photos);
              } else {
                this.networkPhotos.data = photos;
              }
              this.networkPhotos.page += 1;
              this.networkPhotos.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
            }
            return this.networkPhotos.data;
          });
      } return DriveService.getDesktopNetworkPhotos(this.networkPhotos.page, '')
        .then((response) => {
          if (response.success) {
            const photos = response.results.data.items.map((item) => ({
              ...item.file as IMedia,
              sharedTime: item.shared,
              root: item.root,
              shareSettings: item.shareSettings,
            }));
            if (this.networkPhotos.page > 0) {
              this.networkPhotos.data.push(...photos);
            } else {
              this.networkPhotos.data = photos;
            }
            this.networkPhotos.page += 1;
            this.networkPhotos.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
          }
          return this.networkPhotos.data;
        });
    },

    async fetchNetworkFiles(page: number): Promise<IResponse<IData<IList<IMixedData>>>> {
      return DriveService.getNetworkFiles(page, this.search).then((response) => {
        if (response.success) {
          const files = response.results.data.items
            .map((item) => ({ ...item.file as IMedia, sharedTime: item.shared, root: item.root }));
          if (page > 0) {
            this.networkFiles.push(...files);
          } else {
            this.networkFiles = files;
          }
        }
        return response;
      });
    },

    async fetchMixedData(): Promise<IMixedData[]> {
      this.dataLoading.mixed = true;
      const mainStore = useMainStore();
      if (mainStore.isMobile) {
        return DriveService.getMixedData(this.mixedData.page, '')
          .then((response) => {
            if (response.success) {
              const mixed = response.results.data.items.filter((mix) => !mix.deleted);
              if (this.mixedData.page > 0) {
                this.mixedData.data.push(...mixed);
              } else {
                this.mixedData.data = mixed;
              }
              this.mixedData.page += 1;
              this.mixedData.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
            }
            this.dataLoading.mixed = false;
            return this.mixedData.data;
          });
      } return DriveService.getDesktopMixedData(this.mixedData.page, '')
        .then((response) => {
          if (response.success) {
            const mixed = response.results.data.items.filter((mix) => !mix.deleted);
            if (this.mixedData.page > 0) {
              this.mixedData.data.push(...mixed);
            } else {
              this.mixedData.data = mixed;
            }
            this.mixedData.page += 1;
            this.mixedData.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
          }
          this.dataLoading.mixed = false;
          return this.mixedData.data;
        });
    },

    async fetchNetworkCases(loader = false, initial = false): Promise<ICase[]> {
      this.dataLoading.networkCases = loader;
      return DriveService.getNetworkCases(this.networkCases.page, '').then((response) => {
        if (response.success) {
          const cases = response.results.data.items.map((item) => ({
            ...item.case as ICase,
            sharedTime: item.shared,
            root: item.root,
            shareSettings: item.shareSettings,
            unShareable: item.unShareable,
          }));
          if (initial) { this.networkCases.page = 0; }
          if (this.networkCases.page > 0) {
            this.networkCases.data.push(...cases);
          } else {
            this.networkCases.data = cases;
          }

          this.networkCases.page += 1;
          this.networkCases.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
        }
        this.dataLoading.networkCases = false;
        return this.networkCases.data;
      });
    },

    async fetchNetworkMixedData(initial = false): Promise<IMixedData[]> {
      function setSharedTime(mix: IMixedData): IMixedData {
        if (mix.case) {
          // eslint-disable-next-line no-param-reassign
          mix.case.sharedTime = mix.shared;
          // eslint-disable-next-line no-param-reassign
          mix.case.root = mix.root;
          // eslint-disable-next-line no-param-reassign
          mix.case.shareSettings = mix.shareSettings;
        } else
        if (mix.file) {
          // eslint-disable-next-line no-param-reassign
          mix.file.sharedTime = mix.shared;
          // eslint-disable-next-line no-param-reassign
          mix.file.root = mix.root;
          // eslint-disable-next-line no-param-reassign
          mix.file.shareSettings = mix.shareSettings;
        }
        return mix;
      }

      const mainStore = useMainStore();
      if (mainStore.isMobile) {
        return DriveService.getNetworkMixedData(this.networkMixedData.page, '')
          .then((response) => {
            if (response.success) {
              const mix = response.results.data.items.map((item) => setSharedTime(item));
              if (initial) { this.networkMixedData.page = 0; }
              if (this.networkMixedData.page > 0) {
                this.networkMixedData.data.push(...mix);
              } else {
                this.networkMixedData.data = mix;
              }
              this.networkMixedData.page += 1;
              this.networkMixedData.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
            }
            return this.networkMixedData.data;
          });
      } return DriveService.getDesktopNetworkMixedData(this.networkMixedData.page, '')
        .then((response) => {
          if (response.success) {
            const mix = response.results.data.items.map((item) => setSharedTime(item));
            if (this.networkMixedData.page > 0) {
              this.networkMixedData.data.push(...mix);
            } else {
              this.networkMixedData.data = mix;
            }
            this.networkMixedData.page += 1;
            this.networkMixedData.lastPage = Math.floor(response.results.data.paging.items / response.results.data.paging.itemsOnPage) + 1;
          }
          return this.networkMixedData.data;
        });
    },

    // setCurrentCaseSharedLabels(): void {
    //   const spacesStore = useSpacesStore();
    //   this.currentCase.sharedLabels = [];

    //   if (this.currentCase.share) {
    //     if (this.currentCase.share.everyone) {
    //       this.currentCase.sharedLabels = everyoneToLables(spacesStore.myContacts, spacesStore.myGroups);
    //     } else {
    //       this.currentCase!.sharedLabels = [
    //         ...contactsToLables(spacesStore.myContacts, this.currentCase.share.sharedAccounts.map((shareId) => shareId.internalId)),
    //         ...groupsToLables(spacesStore.myGroups, this.currentCase.share.sharedGroups),
    //       ];
    //     }
    //   }
    // },

    // async fetchSharedMediaInfo(id: string, isPhoto = false): Promise<ISharedLable[]> {
    //   const spacesStore = useSpacesStore();
    //   console.log('FETCH_SHARED_MEDIA_INFO');
    //   return DriveService.fetchSharedMediaInfo(id).then((response) => {
    //     if (isPhoto) {
    //       this.fullscreenPhoto!.sharedLabels = [];
    //     } else {
    //       this.fullscreenFile!.sharedLabels = [];
    //     }

    //     if (response.success && response.results.data) {
    //       if (response.results.data.everyone) {
    //         if (isPhoto) {
    //           this.fullscreenPhoto!.sharedLabels = everyoneToLables(spacesStore.myContacts, spacesStore.myGroups);
    //         } else {
    //           this.fullscreenFile!.sharedLabels = everyoneToLables(spacesStore.myContacts, spacesStore.myGroups);
    //         }
    //       } else if (isPhoto) {
    //           this.fullscreenPhoto!.sharedLabels = [
    //             ...contactsToLables(spacesStore.myContacts, response.results.data.sharedAccounts.map((shareId) => shareId.internalId)),
    //             ...groupsToLables(spacesStore.myGroups, response.results.data.sharedGroups),
    //           ];
    //       } else {
    //           this.fullscreenFile!.sharedLabels = [
    //             ...contactsToLables(spacesStore.myContacts, response.results.data.sharedAccounts.map((shareId) => shareId.internalId)),
    //             ...groupsToLables(spacesStore.myGroups, response.results.data.sharedGroups),
    //           ];
    //       }
    //     }
    //     return isPhoto ? this.fullscreenPhoto!.sharedLabels! : this.fullscreenFile!.sharedLabels!;
    //   });
    // },

    // async fetchSharedFolderInfo(id: string): Promise<ISharedLable[]> {
    //   const spacesStore = useSpacesStore();

    //   return DriveService.fetchSharedFolderInfo(id).then((response) => {
    //     this.currentFolder!.sharedLabels = [];
    //     if (response.success && response.results.data) {
    //       if (response.results.data.everyone) {
    //         this.currentFolder!.sharedLabels = everyoneToLables(spacesStore.myContacts, spacesStore.myGroups);
    //       } else {
    //         this.currentFolder!.sharedLabels = [
    //           ...contactsToLables(spacesStore.myContacts, response.results.data.sharedAccounts.map((shareId) => shareId.internalId)),
    //           ...groupsToLables(spacesStore.myGroups, response.results.data.sharedGroups),
    //         ];
    //       }
    //     }
    //     return this.currentFolder!.sharedLabels!;
    //   });
    // },

    // async fetchWhereFileUsing(fileId: string, isPhoto: boolean): Promise<void> {
    //   return DriveService.getWhereFileUsing(fileId)
    //     .then((response) => {
    //       const cases = response.results.data;
    //       if (isPhoto) {
    //         this.fullscreenPhoto!.whereUsed = cases;
    //       } else {
    //         this.fullscreenFile!.whereUsed = cases;
    //       }
    //     });
    // },

    async removeFilesFromCase(filesPayload: ICaseFilesManage) {
      const globalCase = this.cases.data.find((cas) => cas.id === filesPayload.id);
      filesPayload.fileIds.forEach((id) => {
        if (globalCase) {
          globalCase.photos = globalCase.photos.filter((ph) => ph.id !== id);
          globalCase.files = globalCase.files.filter((fl) => fl.id !== id);
        }

        if (this.currentCase.id === filesPayload.id) {
          this.currentCase.photos = this.currentCase.photos.filter((ph) => ph.id !== id);
          this.currentCase.files = this.currentCase.files.filter((fl) => fl.id !== id);

          this.cases.data = [this.currentCase, ...this.cases.data.filter((cas) => cas.id !== this.currentCase.id)];
        }

        this.photoList = this.photoList.filter((ph) => ph.id !== id);
      });

      return MediaService.deleteMediaFromCase(filesPayload);
    },

    async addFilesToCase(filesPayload: ICaseFilesManage) {
      const newCase = this.cases.data.find((cas) => cas.id === filesPayload.id);
      if (newCase) {
        filesPayload.fileIds.forEach((fileId) => {
          const file = this.photos.data.find((photo) => photo.id === fileId) as IMedia || this.files.find((item) => item.id === fileId) as IMedia;
          if (file.type !== MEDIA_TYPES.FILES) {
            this.photos.data.find((photo) => photo.contentId === file.contentId)!.whereUsed = file.whereUsed;
            newCase.photos = [file, ...newCase.photos];
            if (this.currentCase.id === filesPayload.id) {
              this.currentCase.photos = [file, ...this.currentCase.photos.filter((item) => item.id !== file.id)];
            }
          } else {
            newCase.files = [file, ...newCase.files];
            if (this.currentCase.id === filesPayload.id) {
              this.currentCase.files = [file, ...this.currentCase.files.filter((item) => item.id !== file.id)];
            }
          }
          this.mixedData.data.forEach((item) => {
            if (item.file?.contentId === file.contentId) {
              // eslint-disable-next-line no-param-reassign
              item.file.whereUsed = file.whereUsed;
            }
          });
        });
        this.cases.data = [newCase, ...this.cases.data.filter((cas) => cas.id !== newCase.id)];
      }
      return MediaService.addFilesToCase(filesPayload);
    },

    removeSharingInfo(currentItem: IMedia | ICase | IFolder, label: ISharedLable) {
      const messagesStore = useMessagesStore();

      const filterShare = (iShare: IShared) => {
        if (label.isGroup) {
          // eslint-disable-next-line no-param-reassign
          iShare.sharedGroups = iShare.sharedGroups.filter((id) => id !== label.id);
        } else {
          // eslint-disable-next-line no-param-reassign
          iShare.sharedAccounts = iShare.sharedAccounts.filter((id) => id.internalId !== label.id);
        }
        const hasSharing = !!(iShare.sharedGroups.length + iShare.sharedAccounts.length);

        return hasSharing ? iShare : null;
      };

      const updateFile = (media: IMedia) => {
        this.mixedData.data = this.mixedData.data.map((item) => {
          if (item.file?.contentId === media.contentId) {
            return {
              ...item,
              file: { ...item.file, share: filterShare(item.file.share!) },
            };
          }
          return item;
        });

        if (media.folderId) {
          this.cases.data = this.cases.data.map((cas) => {
            if (cas.id === media.folderId) {
              return {
                ...cas,
                files: cas.files.map((item) => {
                  if (item.contentId === media.contentId) {
                    return { ...item, share: filterShare(item.share!) };
                  }
                  return item;
                }),
              };
            }
            return cas;
          });
        }

        if (this.fullscreenFile) {
          this.fullscreenFile.share = filterShare(this.fullscreenFile.share!);
        }

        if (this.appPhotoViewMedia) {
          this.appPhotoViewMedia.share = filterShare(this.appPhotoViewMedia.share!);
        }
      };

      const updatePhoto = (media: IMedia) => {
        this.mixedData.data = this.mixedData.data.map((item) => {
          if (item.file?.contentId === media.contentId) {
            return {
              ...item,
              file: { ...item.file, share: filterShare(item.file.share!) },
            };
          }
          return item;
        });

        this.photos.data = this.photos.data.map((item) => {
          if (item?.contentId === media.contentId) {
            return {
              ...item,
              share: filterShare(item.share!),
            };
          }
          return item;
        });

        this.photoList = this.photoList.map((item) => {
          if (item?.contentId === media.contentId) {
            return {
              ...item,
              share: filterShare(item.share!),
            };
          }
          return item;
        });

        if (media.folderId) {
          this.cases.data = this.cases.data.map((cas) => {
            if (cas.id === media.folderId) {
              return {
                ...cas,
                photos: cas.photos.map((item) => {
                  if (item.contentId === media.contentId) {
                    return {
                      ...item,
                      share: filterShare(item.share!),
                    };
                  }
                  return item;
                }),
              };
            }
            return cas;
          });
        }

        if (this.fullscreenPhoto) {
          this.fullscreenPhoto.share = filterShare(this.fullscreenPhoto.share!);
        }

        if (this.appPhotoViewMedia) {
          this.appPhotoViewMedia.share = filterShare(this.appPhotoViewMedia.share!);
        }
      };

      const updateFolder = (folder: IFolder) => {
        this.mixedData.data = this.mixedData.data.map((item) => {
          if (item.id === folder.id) {
            return {
              ...item,
              folder: { ...item.folder!, share: filterShare(item.folder!.share!) },
            };
          }
          return item;
        });
      };

      const updateCase = (iCase: ICase) => {
        this.mixedData.data = this.mixedData.data.map((item) => {
          if (item.id === iCase.id) {
            return {
              ...item,
              case: { ...item.case!, share: filterShare(item.case!.share!) },
            };
          }
          return item;
        });

        this.cases.data = this.cases.data.map((item) => {
          if (item.id === iCase.id) {
            return ({
              ...item,
              case: { ...iCase, share: filterShare(iCase.share!) },
            });
          }
          return item;
        });
      };

      const updateConversation = () => {
        if (messagesStore.currentConversation?.info.id === label.id) {
          messagesStore.currentConversation.messages = messagesStore.currentConversation.messages.map(
            (message) => {
              if (message.attachment?.id === currentItem.id) {
                let notSharedText = '';

                if ((currentItem as IMedia).type) {
                  notSharedText = `Media from ${message.owner.firstName} ${message.owner.lastName} is not shared anymore`;
                } else if ((currentItem as IFolder).folderType) {
                  notSharedText = `Folder from ${message.owner.firstName} ${message.owner.lastName} is not shared anymore`;
                } else {
                  notSharedText = `Case from ${message.owner.firstName} ${message.owner.lastName} is not shared anymore`;
                }

                return {
                  ...message,
                  attachment: null,
                  content: notSharedText,
                };
              }
              return message;
            },
          );
        }
      };

      if ((currentItem as IMedia).type) {
        const media = currentItem as IMedia;
        if (media.type === MEDIA_TYPES.FILES) {
          updateFile(media);
        } else {
          updatePhoto(media);
        }
      } else if ((currentItem as IFolder).folderType) {
        const folder = currentItem as IFolder;
        updateFolder(folder);
      } else {
        const iCase = (currentItem as ICase);
        updateCase(iCase);
      }

      updateConversation();

      const payload = {
        id: currentItem.id!,
        accessType: label.isGroup ? SHARE_ACCESS.GROUP : SHARE_ACCESS.SPECIFIC_PEOPLE,
        target: label.id,
      };
      DriveService.removeShare(payload);
    },

    async createCase(payload: ISaveCasePayload, onUpdate?: (percent: number) => void): Promise<ICase> {
      this.resetCurrentCase();
      return DriveService.createCase(payload, onUpdate).then((response) => {
        const iCase = response.results.data;
        const mix: IMixedData = {
          case: { ...iCase },
          id: iCase.id!,
          type: MIXED_DATA_TYPE.CASE,
        };
        /**
         * commented because create case object with wrong data,
         * after case created it should be fetched from BE in view mode
         * */
        this.cases.data = [{ ...iCase }, ...this.cases.data];
        if (!payload.folderId) {
          this.mixedData.data = [{ ...mix }, ...this.mixedData.data];
        }
        return { ...iCase };
      });
    },

    async saveFileLabels(isPhoto: boolean) {
      const mainStore = useMainStore();
      mainStore.tagsToLabelInput(true).then((labels) => {
        const payload: IImageAddLabels = {
          id: isPhoto ? this.fullscreenPhoto!.id : this.fullscreenFile!.id,
          labelIds: labels.map((lab) => lab.id),
          photoCrop: null,
        };
        DriveService.addImageLabels(payload).then(() => {
          if (isPhoto) {
            this.fullscreenPhoto!.labels = labels;
          } else {
            this.fullscreenFile!.labels = labels;
          }
        });
      });
    },

    async updateCase(onUpdate?: (percent: number) => void): Promise<IResponse<IData<ICase>>> {
      const mainStore = useMainStore();

      const payload: ISaveCasePayload = {
        id: this.currentCase.id!,
        title: this.bufferCurrentCaseTitle.trim() || this.currentCase.title.trim(),
        description: this.currentCase.description,
        patientId: this.currentCase.patientId,
        patientName: this.currentCase.patientName,
        fileIds: [],
        labelIds: [],
        folderId: this.currentCase.folderId || undefined,
      };

      // remove 343 to 348 after BE check, if required and check line 356
      // const temp = { ...this.currentCase } as ISaveCasePayload;
      // temp.fileIds = [
      //   ...this.currentCase.photos.map((el) => el.id),
      //   ...this.currentCase.files.map((el) => el.id),
      // ];
      //
      // temp.labelIds = [...this.currentCase.labels.map((el) => el.id)];

      this.currentCase.title = this.bufferCurrentCaseTitle.trim();
      this.currentCase.photos.forEach((photo) => { payload.fileIds!.push(photo.id); });
      this.currentCase.files.forEach((files) => { payload.fileIds!.push(files.id); });

      this.cases.data = this.cases.data.map((cas) => {
        if (cas.id === this.currentCase.id) {
          return this.currentCase;
        }
        return cas;
      });
      this.mixedData.data = this.mixedData.data.map((mix) => {
        if (mix.id === this.currentCase.id) {
          return {
            ...mix,
            case: this.currentCase,
          };
        }
        return mix;
      });

      await mainStore.tagsToLabelInput().then((labels) => { payload.labelIds = labels.map((lab) => lab.id); });

      // change temp to payload
      return DriveService.updateCase(payload, onUpdate);
    },

    async deleteCase(caseId?: string): Promise<IResponse> {
      const id = caseId || this.currentCase.id;
      return DriveService.deleteCase(id).then((response) => {
        if (response.success) {
          this.cases.data = this.cases.data.filter((iCase) => iCase.id !== id);
          this.mixedData.data = this.mixedData.data.filter((mixed) => mixed.id !== id);
          this.removeFromMessages(id!);
          this.resetCurrentCase();
        }
        return response;
      });
    },

    addCaseToStorage(caseData: ICase) {
      const mixed: IMixedData = {
        id: caseData.id!,
        type: MIXED_DATA_TYPE.CASE,
        case: caseData,
      };

      if (!caseData.folderId) {
        const notFolderIdx = this.mixedData.data.findIndex((item) => !item.folder);
        if (notFolderIdx !== -1) {
          this.mixedData.data.splice(notFolderIdx, 0, mixed);
        }
      }

      this.cases.data = [caseData, ...this.cases.data];
    },

    addMediaToStorage(mediaData: IMedia) {
      const mixed: IMixedData = {
        id: mediaData.id!,
        type: MIXED_DATA_TYPE.FILE,
        file: mediaData,
      };

      if (!mediaData.folderId) {
        const notFolderIdx = this.mixedData.data.findIndex((item) => !item.folder);
        if (notFolderIdx !== -1) {
          this.mixedData.data.splice(notFolderIdx, 0, mixed);
        }
      }

      if (mediaData.type !== MEDIA_TYPES.FILES) {
        this.photos.data = [mediaData, ...this.photos.data];
      } else {
        this.files = [mediaData, ...this.files];
      }
    },

    async saveCaseAsCopy(caseId?: string): Promise<IResponse<IData<ICase>>> {
      const id = caseId || this.currentCase.id;
      return DriveService.saveCaseAsCopy(id).then((response) => {
        if (response.success) {
          this.addCaseToStorage(response.results.data);
        }
        return response;
      });
    },

    async saveMediaAsCopy(id: string): Promise<IResponse<IData<IMedia>>> {
      return DriveService.saveMediaAsCopy(id).then((response) => {
        if (response.success) {
          this.addMediaToStorage(response.results.data);
        }
        return response;
      });
    },

    async saveFolderAsCopy(id: string): Promise<IResponse> {
      return DriveService.saveFolderAsCopy(id).then((response) => {
        if (response.success) {
          // console.log(response);
        }
        return response;
      });
    },

    removeFile(id: string) {
      this.files = this.files.filter((file) => file.id !== id);
      this.mixedData.data = this.mixedData.data.filter((mixed) => mixed.id !== id);
      if (this.currentFolder?.mixedData) {
        this.currentFolder.mixedData = this.currentFolder.mixedData.filter((photo) => photo.id !== id);
      }
      this.fullscreenFile = this.fullscreenFile?.id === id ? undefined : this.fullscreenFile;
    },

    removePhoto(id: string) {
      this.photos.data = this.photos.data.filter((photo) => photo.id !== id);
      this.mixedData.data = this.mixedData.data.filter((mixed) => mixed.id !== id);
      if (this.currentFolder?.mixedData) {
        this.currentFolder.mixedData = this.currentFolder.mixedData.filter((mixed) => mixed.id !== id);
      }
      this.fullscreenPhoto = this.fullscreenPhoto?.id === id ? undefined : this.fullscreenPhoto;
    },

    removeVideo(id: string) {
      this.photos.data = this.photos.data.filter((photo) => photo.id !== id);
      this.mixedData.data = this.mixedData.data.filter((mixed) => mixed.id !== id);
      if (this.currentFolder?.mixedData) {
        this.currentFolder.mixedData = this.currentFolder.mixedData.filter((mixed) => mixed.id !== id);
      }
      this.fullscreenFile = undefined;
    },

    removeFromMessages(id: string) {
      const messagesStore = useMessagesStore();
      if (messagesStore.currentConversation?.messages.length) {
        const filtered = messagesStore.currentConversation.messages.map((msg) => {
          if (msg.attachment?.id === id) {
            let notSharedText = '';

            if (msg.attachmentType === MESSAGE_ATTACHMENT_TYPE.MEDIA) {
              notSharedText = `Media from ${msg.owner.firstName} ${msg.owner.lastName} is not shared anymore`;
            } else if (msg.attachmentType === MESSAGE_ATTACHMENT_TYPE.CASE) {
              notSharedText = `Case from ${msg.owner.firstName} ${msg.owner.lastName} is not shared anymore`;
            }

            return {
              ...msg,
              attachment: null,
              content: notSharedText,
            };
          }
          return msg;
        });
        messagesStore.currentConversation.messages = filtered;
      }
    },

    async deleteMedia(mediaId: string, type: MEDIA_TYPES): Promise<IResponse> {
      const mainStore = useMainStore();
      const removeFileFromStorage = () => {
        switch (type) {
          case MEDIA_TYPES.FILES:
            this.removeFile(mediaId);
            break;
          case MEDIA_TYPES.PHOTO:
            this.removePhoto(mediaId);
            break;
          case MEDIA_TYPES.VIDEO:
            this.removeVideo(mediaId);
            break;
          default:
            break;
        }
      };

      removeFileFromStorage();
      this.removeFromMessages(mediaId);
      mainStore.subscriptionQuota.filesQuota.current -= 1;
      return MediaService.deleteMedia(mediaId);
    },

    async getFolderById(page: number, folderId: string): Promise<IResponse<IData<IList<IMixedData>>>> {
      if (page > 0) {
        return DriveService.getMixedData(page, this.search, folderId).then((response) => {
          const mixed = response.results.data.items.filter((mix) => !mix.deleted);
          this.currentFolder!.mixedData!.push(...mixed);
          return response;
        });
      }
      const currentSearch = this.search;
      return Promise.all([DriveService.getFolderById(folderId), DriveService.getMixedData(page, this.search, folderId)]).then((response) => {
        if (currentSearch !== this.search) {
          return Promise.reject(new Error('response is out of date'));
        }
        if (response[0].success) {
          this.currentFolder = response[0].results.data;

          if (response[1].success) {
            const mixed = response[1].results.data.items.filter((mix) => !mix.deleted);
            this.currentFolder.mixedData = mixed;
          }
        }

        return response[1];
      });
    },

    async renameFolder(name: string, id: string) {
      const payload: ICreateFolder = { name, id };
      let prevName = '';

      const mixItem = this.mixedData.data.find((item) => item.folder?.id === id);
      if (mixItem?.folder) {
        prevName = mixItem.folder.name;
        mixItem.folder.name = name;
      }

      const foldMixItem = this.currentFolder?.mixedData?.find((item) => item.folder?.id === id);
      if (foldMixItem?.folder) {
        prevName = foldMixItem.folder.name;
        foldMixItem.folder.name = name;
      }

      return DriveService.createFolder(payload).catch((e) => {
        if (mixItem?.folder) {
          mixItem.folder.name = prevName;
        }

        if (foldMixItem?.folder) {
          foldMixItem.folder.name = prevName;
        }
        return e;
      });
    },

    async renameFile(fileName: string, id: string) {
      const payload = {
        fileName,
        id,
        saveMode: 'COMMON',
      };
      let prevName = '';

      const mixItem = this.mixedData.data.find((item) => item.file?.id === id);
      if (mixItem?.file) {
        prevName = mixItem.file.name;
        mixItem.file.name = fileName;
      }

      const photoItem = this.photos.data.find((item) => item?.id === id);
      if (photoItem) {
        prevName = photoItem.name;
        photoItem.name = fileName;
      }

      return DriveService.renameMedia(payload).catch((e) => {
        if (mixItem?.file) {
          mixItem.file.name = prevName;
        }
        return e;
      });
    },

    async createFolder(name: string, upperFolderId?: string): Promise<IFolder> {
      const payload: ICreateFolder = { name };
      if (upperFolderId) {
        payload.upperFolderId = upperFolderId;
      }

      return DriveService.createFolder(payload).then((response) => {
        const folder: IMixedData = {
          folder: response.results.data,
          id: response.results.data.id,
          type: MIXED_DATA_TYPE.FOLDER,
        };

        if (!upperFolderId) {
          this.mixedData.data = [{ ...folder }, ...this.mixedData.data];
        } else if (this.currentFolder?.id === upperFolderId) {
          if (this.currentFolder.mixedData) {
            this.currentFolder.mixedData = [{ ...folder }, ...this.currentFolder.mixedData];
          } else {
            this.currentFolder.mixedData = [{ ...folder }];
          }
        }
        return response.results.data;
      });
    },

    async deleteFolder(folderId: string): Promise<IResponse> {
      return DriveService.deleteFolder(folderId).then((response) => {
        if (response.success) {
          this.mixedData.data = this.mixedData.data.filter((folder) => folder.id !== folderId);
          if (this.currentFolder?.mixedData) {
            this.currentFolder.mixedData = this.currentFolder.mixedData.filter((folder) => folder.id !== folderId);
          }
        }
        return response;
      });
    },

    resetCurrentFolder() {
      this.currentFolder = null;
    },

    async getCaseById(id: string, isNetwork = false, isAfterCreate = false): Promise<ICase> {
      const currentCase = (isNetwork ? this.networkCases.data : this.cases.data).find((cas) => cas.id === id);
      /**
       * if we come to case view after create case, we should skip to load it from store cases list, because of wrong images ids
       * instead we should get fresh data and update it in driveStore.cases.data thereafter
       * */
      if (currentCase && !isAfterCreate) {
        return { ...currentCase };
      }

      return DriveService.getCaseById(id)
        .then((response) => ({ ...response.results.data, photos: [...response.results.data.videos, ...response.results.data.photos], videos: [] }));
    },

    async getPublicCaseByUrl(publicUrl: string): Promise<ICase> {
      return MediaService.getPublicCaseByUrl(publicUrl)
        .then((response) => ({ ...response.results.data, photos: [...response.results.data.photos], videos: [...response.results.data.videos] }));
    },

    async makeCasePublic(id: string): Promise<any> {
      return DriveService.makeCasePublic(id)
        .then((response) => ({ ...response }));
    },

    async unmakeCasePublic(id: string): Promise<any> {
      return DriveService.unmakeCasePublic(id)
        .then((response) => ({ ...response }));
    },

    /** Remove this from store, call DriveService directly instead */
    async getCaseExternalUrl(id: string): Promise<any> {
      return DriveService.getCaseExternalUrl(id)
        .then((response) => ({ ...response }));
    },

    getEncodingMedias(encodingType: ENCODING_TYPES): IMedia[] {
      switch (encodingType) {
        case ENCODING_TYPES.CURRENT_CASE_PHOTO:
          return this.currentCase.photos;

        case ENCODING_TYPES.CURRENT_CASE_FILES:
          return this.currentCase.files;

        case ENCODING_TYPES.ALL_PHOTO:
          return this.photos.data;

        default:
          return this.files;
      }
    },

    resetCurrentCase(): void {
      this.currentCase = {
        id: null,
        created: null,
        updated: null,
        title: '',
        description: '',
        patientId: '',
        patientName: '',
        notes: '',
        files: [],
        labels: [],
        photos: [],
        videos: [],
        folder: {},
        folderId: null,
        owner: {} as ISpacesContact,
        ownerId: {
          internalId: '',
        },
        share: null,
        publicAccessed: false,
        publicUrl: '',
        root: false,
        profile: {} as IProfile,
      };

      this.bufferCurrentCaseTitle = '';
      this.caseTitleValidation = '';
    },

    resetCases(): void {
      this.cases.data = [];
      this.cases.lastPage = 1;
      this.cases.page = 0;
    },
  },
});
